// phase-ui-compiler-tree-documents-pane — badge sets are keyed by
// sectionKey() (doc codes are bare-numeric, node codes m-prefixed).
import { sectionKey } from '@/lib/sectionKey';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactElement,
} from 'react';
import type { SectionNode } from '@/lib/api/compiler';
import { DocumentBadge } from './DocumentBadge';
import { PaneHeaderRow } from './paneChrome';
import { docReviewFlags } from '@/lib/docReviewFlags';
import { LIFECYCLE_VISUAL, parseLifecycleOperation } from './lifecycle';
import { LockStateIcon } from './LockStateIcon';
import type { DocumentDropPosition } from './documentDrop';

/**
 * Wave E.1 — Left-pane CTD section tree.
 *
 * Renders a hierarchical, expandable tree of eCTD section codes (m1.1,
 * m2.7.1, etc.) and reports clicks up to the parent so the middle pane
 * can refresh its document list.
 *
 * State semantics:
 *   - `selectedCode` lives in the parent (CompilerPage) so it can be
 *     used as the `?sectionCode=` filter when fetching documents.
 *   - `expanded` is purely UI state and lives here; defaulting to
 *     Module 1 expanded gives the user a useful starting view.
 */

export interface SectionTreeProps {
  readonly tree: readonly SectionNode[];
  readonly selectedCode: string | null;
  readonly onSelect: (code: string) => void;
  readonly loading?: boolean;
  readonly error?: string | null;
  /** Optional title to render above the tree. Defaults to "CTD Tree". */
  readonly title?: string;
  /** Optional subtitle (e.g. "FDA M1 v3.3 (fallback)") shown below the title. */
  readonly subtitle?: string;
  /**
   * Phase UI-13 — drop-file handler. Called when a user drags OS
   * files onto a section node or document edge. The parent receives the files together
   * and is responsible for creating + uploading the Document.
   */
  readonly onDropFiles?: (sectionCode: string, files: readonly File[], position?: DocumentDropPosition) => void;
  /**
   * TODO-081 — cross-section drag-reclassify handler. Called when a
   * document row (carrying `application/x-document-id`) is dropped onto
   * a section node. The parent receives `(documentId, targetSectionCode)`
   * and is responsible for the confirmation dialog + PATCH call.
   */
  readonly onDropDocument?: (documentId: string, targetSectionCode: string, position?: DocumentDropPosition) => void;
  /**
   * TODO-065 — Region string (e.g. "FDA", "US", "EU"). When the region is
   * FDA/US, empty required M1 sections show a red "required" indicator.
   */
  readonly region?: string;
  /**
   * Phase node-extension-compiler-ui (TODO-048 Part B) — set of section codes
   * that have node extensions defined. When a node's code is in this set, a
   * small "⊕ ext" badge is rendered to indicate custom sub-sections exist.
   */
  readonly extensionCodes?: ReadonlySet<string>;
  /**
   * Phase phase-review-surface-strengthen — set of section codes that have at
   * least one document with no human confirmation yet (ConfirmedAt = null).
   * Renders an amber "⚠ review" badge so reviewers can scan the tree and
   * immediately see where attention is needed.
   */
  readonly unconfirmedCodes?: ReadonlySet<string>;
  /**
   * Phase phase-review-surface-strengthen — set of section codes that have at
   * least one low-confidence placement (confidence < 40). Renders an orange
   * "◐ low conf." badge to surface uncertain AI placements prominently.
   */
  readonly lowConfidenceCodes?: ReadonlySet<string>;
  /**
   * phase-ui-compiler-tree-document-leaves — documents per sectionKey().
   * When provided, sections expand down to DOCUMENT leaves (the tree
   * becomes a live ToC with lifecycle annotations) and count badges show
   * the number of leaves under each section.
   */
  readonly documentsByKey?: ReadonlyMap<string, readonly SectionTreeDocLeaf[]>;
  /** Unclassified documents, rendered under an "Unplaced" root node. */
  readonly unplacedDocuments?: readonly SectionTreeDocLeaf[];
  readonly selectedDocumentId?: string | null;
  /** Leaf click → same handler as a middle-pane document click. */
  readonly onSelectDocument?: (documentId: string) => void;
  /** Replace/delete target doc id → its sequence number (e.g. "0000"). */
  readonly priorSeqByDocId?: ReadonlyMap<string, string>;
  /** sessionStorage key — remembers expand state for the session. */
  readonly storageKey?: string;
  /**
   * phase-compiler-chrome-batch item 4 — placement move-up/move-down for a
   * document leaf, INLINE in the tree. Wired by CompilerPage to the same
   * swap+reorder flow as the Workspace placement cards (shared `swapAdjacent`
   * + the same reorder endpoint + the same undo push). When absent (read-only
   * viewers) no arrow slot renders at all.
   */
  readonly onMoveDocument?: (documentId: string, direction: 'up' | 'down') => void;
}

/** Minimal document shape a tree leaf needs (structurally satisfied by the API Document). */
export interface SectionTreeDocLeaf {
  readonly Id: string;
  readonly FileName: string;
  /** Owning sequence, displayed on each document rather than on section nodes. */
  readonly SequenceNumber?: string | null;
  readonly LifecycleOperation?: string | null;
  readonly ReplacesDocumentId?: string | null;
  readonly IsLocked?: boolean;
  /** phase-ui-tree-leaf-review-capsules — review-state fields. `null` means
   *  present-but-unconfirmed; `undefined` (absent) means unknown → no capsule. */
  readonly ConfirmedAt?: string | null;
  /**
   * phase-compiler-screen-batch item 2 — the AUTHORITATIVE classifier
   * confidence (0–1 float). `ConfidenceScore` below is the legacy column, left
   * at 0 by the current classifier; reading only that one is what made the tree
   * badge contradict the Inspector. Resolution lives in `lib/confidence`.
   */
  readonly ClassificationConfidence?: number | null;
  readonly ConfidenceScore?: number | null;
}

// phase-tree-lock-open-shackle — the fixed-width slot that keeps the dense rows
// from reflowing as lock state changes, and aligns a row with an icon against
// one without. BOTH states now occupy the same slot at the same colour and
// weight; they differ only in the glyph's shackle (see LockStateIcon), so the
// old opacity-0.45 "unlocked" style — which distinguished the states by weight
// and read as absence — is gone.
const lockIconSlotBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 16,
  flex: '0 0 16px',
  color: '#374151',
  lineHeight: 1,
};

/** Shared capsule styles — identical on section rows and document leaves. */
const lowConfBadgeStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: '#B45309',
  background: 'rgba(245,158,11,0.18)',
  border: '1px solid rgba(245,158,11,0.45)',
  borderRadius: 9,
  padding: '0 5px',
  whiteSpace: 'nowrap',
  lineHeight: '16px',
};
const reviewBadgeStyle: CSSProperties = {
  fontSize: 10,
  fontWeight: 700,
  color: '#92400E',
  background: 'rgba(245,158,11,0.15)',
  border: '1px solid rgba(245,158,11,0.40)',
  borderRadius: 9,
  padding: '0 5px',
  whiteSpace: 'nowrap',
  lineHeight: '16px',
};

const subtitleStyle: CSSProperties = {
  margin: '0 0 12px 0',
  fontSize: 11,
  color: 'var(--text-muted)',
  fontFamily: 'monospace',
};

const nodeBaseStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 4,
  padding: '4px 6px',
  fontSize: 13,
  color: 'var(--text)',
  borderRadius: 4,
  cursor: 'pointer',
  userSelect: 'none',
};

// phase-compiler-tree-conf-align — the section-node label grows to absorb row
// slack (so the count/required/ext/review/low-conf badges right-align) AND
// truncates: a long label must not shove the trailing capsules out of the pane.
// The full label stays available via the row's title. Mirrors docLeafNameStyle.
const nodeLabelStyle: CSSProperties = {
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
};

const nodeSelectedStyle: CSSProperties = {
  background: 'var(--accent-bg, rgba(99,102,241,0.15))',
  color: 'var(--accent, #4f46e5)',
  fontWeight: 600,
};

/** phase-compiler-chrome-batch item 5 — when a DOCUMENT holds the primary
 *  highlight, its containing section shows only this visually-muted tint:
 *  a faint wash, no accent colour, no bold — context, not selection. */
const nodeContainingTintStyle: CSSProperties = {
  background: 'var(--accent-bg-muted, rgba(99,102,241,0.06))',
};

/** item 4 — fixed-width slot for the leaf ▲▼ arrows (the sideIconCell
 *  principle, 942f37c): present on every leaf row while reordering is
 *  offered, so arrow/no-arrow rows never reflow and the title never shifts.
 *  Locked leaves render the slot EMPTY (arrows absent, not disabled). */
const moveSlotStyle: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 1,
  width: 34,
  flex: '0 0 34px',
  justifyContent: 'center',
};

const moveBtnStyle = (enabled: boolean): CSSProperties => ({
  fontSize: 9,
  lineHeight: 1.2,
  padding: '0 3px',
  border: '1px solid var(--border)',
  borderRadius: 3,
  background: 'var(--surface)',
  color: 'var(--text-muted)',
  cursor: enabled ? 'pointer' : 'default',
  opacity: enabled ? 1 : 0.35,
});

const caretStyle: CSSProperties = {
  display: 'inline-block',
  width: 12,
  textAlign: 'center',
  fontSize: 10,
  color: 'var(--text-muted)',
};

const emptyStyle: CSSProperties = {
  padding: '12px 8px',
  fontSize: 13,
  color: 'var(--text-muted)',
  fontStyle: 'italic',
};

const errorStyle: CSSProperties = {
  padding: '12px 8px',
  fontSize: 13,
  color: 'var(--danger, #b91c1c)',
};

/** TODO-060 — document count pill per section node. */
const docCountStyle: CSSProperties = {
  display: 'inline-block',
  marginLeft: 'auto',
  minWidth: 18,
  padding: '0 5px',
  fontSize: 10,
  fontWeight: 600,
  lineHeight: '16px',
  borderRadius: 8,
  background: 'var(--border, #e5e7eb)',
  color: 'var(--text-muted, #6b7280)',
  textAlign: 'center',
};

/** Phase node-extension-compiler-ui (TODO-048 Part B) — "⊕ ext" badge for sections with node extensions. */
const extBadgeStyle: CSSProperties = {
  marginLeft: 4,
  fontSize: 10,
  fontWeight: 600,
  color: 'var(--accent, #4f46e5)',
  letterSpacing: '0.02em',
};

/** TODO-065 — "required" indicator for empty FDA M1 sections. */
const requiredBadgeStyle: CSSProperties = {
  marginLeft: 4,
  fontSize: 10,
  fontWeight: 600,
  color: '#b91c1c',
  letterSpacing: '0.02em',
};

/**
 * FDA Module 1 sections required per FDA CTOC:
 * 1.1 Forms, 1.2 Cover Letter, 1.3 Administrative Information.
 */
const FDA_REQUIRED_M1 = new Set(['m1.1', 'm1.2', 'm1.3']);

/** Sentinel expand-state code for the Unplaced pseudo-node. */
const UNPLACED_CODE = '__unplaced__';

const docLeafBaseStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '2px 6px',
  fontSize: 12,
  color: 'var(--text)',
  borderRadius: 4,
  cursor: 'pointer',
  userSelect: 'none',
  minWidth: 0,
};

// phase-compiler-tree-conf-align — the filename GROWS to absorb row slack
// (like the section-node label's `flex: 1`), so the trailing metadata cluster —
// operation badge, lock slot, target, review capsules — right-aligns instead of
// hugging the variable-length filename at an arbitrary x. A long name truncates
// here (overflow/ellipsis + minWidth:0) rather than pushing the badges offscreen.
const docLeafNameStyle: CSSProperties = {
  flex: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  minWidth: 0,
};

const docLeafTargetStyle: CSSProperties = {
  fontSize: 10,
  color: 'var(--text-muted)',
  whiteSpace: 'nowrap',
};

interface DocLeafRowProps {
  readonly doc: SectionTreeDocLeaf;
  readonly depth: number;
  readonly selected: boolean;
  readonly onSelectDocument?: ((documentId: string) => void) | undefined;
  readonly priorSeqByDocId?: ReadonlyMap<string, string> | undefined;
  /** item 4 — inline placement arrows; absent = no slot (read-only mode). */
  readonly onMoveDocument?: ((documentId: string, direction: 'up' | 'down') => void) | undefined;
  /** Section-boundary flags — first/last leaf in its section bucket. */
  readonly canMoveUp?: boolean;
  readonly canMoveDown?: boolean;
  readonly sectionCode?: string;
  readonly onDropFiles?: SectionTreeProps['onDropFiles'];
  readonly onDropDocument?: SectionTreeProps['onDropDocument'];
}

function DocLeafRow({
  doc,
  depth,
  selected,
  onSelectDocument,
  priorSeqByDocId,
  onMoveDocument,
  canMoveUp = false,
  canMoveDown = false,
  sectionCode,
  onDropFiles,
  onDropDocument,
}: DocLeafRowProps): ReactElement {
  const [dropEdge, setDropEdge] = useState<'before' | 'after' | null>(null);
  // Parse without defaulting so an unrecognised value stays "Unknown" — no
  // fabricated NEW, no strike, and no target claim.
  const op = parseLifecycleOperation(doc.LifecycleOperation);
  const strikethrough = op ? LIFECYCLE_VISUAL[op].strikethrough === true : false;
  // Replace/delete leaves show their target sequence when known
  // ("replaces seq 0000"); a linked-but-unresolved target degrades to
  // "prior linked" (the staging table's hint) — never silently nothing. An
  // unrecognised operation makes no target claim at all.
  let target: string | null = null;
  if (doc.ReplacesDocumentId && (op === 'replace' || op === 'delete')) {
    const seq = priorSeqByDocId?.get(doc.ReplacesDocumentId) ?? priorSeqByDocId?.get(doc.ReplacesDocumentId.toLowerCase());
    const verb = op === 'delete' ? 'deletes' : 'replaces';
    target = seq ? `${verb} seq ${seq}` : 'prior linked';
  }
  const flags = docReviewFlags(doc);
  return (
    <div
      role="treeitem"
      aria-selected={selected}
      aria-level={depth + 1}
      tabIndex={0}
      data-testid={`section-tree-doc-${doc.Id}`}
      draggable={Boolean(onDropDocument) && doc.IsLocked !== true}
      onDragStart={(e) => {
        if (!onDropDocument || doc.IsLocked === true) { e.preventDefault(); return; }
        e.dataTransfer.setData('application/x-document-id', doc.Id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragOver={(e) => {
        const hasDoc = e.dataTransfer.types.includes('application/x-document-id');
        const hasFiles = e.dataTransfer.types.includes('Files');
        if (!sectionCode || !(hasDoc ? onDropDocument : hasFiles && onDropFiles)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = hasDoc ? 'move' : 'copy';
        const rect = e.currentTarget.getBoundingClientRect();
        setDropEdge(e.clientY < rect.top + rect.height / 2 ? 'before' : 'after');
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as globalThis.Node | null)) setDropEdge(null);
      }}
      onDrop={(e) => {
        e.preventDefault();
        e.stopPropagation();
        setDropEdge(null);
        if (!sectionCode) return;
        const rect = e.currentTarget.getBoundingClientRect();
        const position: DocumentDropPosition = { documentId: doc.Id, edge: e.clientY < rect.top + rect.height / 2 ? 'before' : 'after' };
        const sourceId = e.dataTransfer.getData('application/x-document-id');
        if (sourceId) {
          if (sourceId !== doc.Id) onDropDocument?.(sourceId, sectionCode, position);
        } else {
          const files = Array.from(e.dataTransfer.files ?? []);
          if (files.length > 0) onDropFiles?.(sectionCode, files, position);
        }
      }}
      onClick={() => onSelectDocument?.(doc.Id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelectDocument?.(doc.Id);
        }
      }}
      style={{
        ...docLeafBaseStyle,
        position: 'relative',
        paddingLeft: 6 + depth * 14,
        ...(selected ? nodeSelectedStyle : null),
      }}
    >
      {dropEdge && (
        <span
          aria-hidden="true"
          data-testid={`tree-insertion-${doc.Id}-${dropEdge}`}
          style={{ position: 'absolute', left: 6 + depth * 14, right: 4, [dropEdge === 'before' ? 'top' : 'bottom']: 0, borderTop: '2px solid #2563EB', pointerEvents: 'none' }}
        />
      )}
      <span style={{ ...caretStyle, fontSize: 9 }} aria-hidden="true">
        └
      </span>
      {/* item 4 — placement ▲▼ INLINE, before the title, in a fixed-width
          slot (sideIconCell principle) so dense rows never reflow between
          arrow and no-arrow states. LOCKED documents render NO arrows —
          absent, not disabled — driven by the same per-doc lock state the
          padlock (LockStateIcon) draws. Boundary rows render a disabled
          arrow, exactly like the Workspace card arrows. */}
      {onMoveDocument && (
        <span style={moveSlotStyle} data-testid={`tree-move-slot-${doc.Id}`}>
          {doc.IsLocked !== true && (
            <>
              <button
                type="button"
                data-testid={`tree-move-up-${doc.Id}`}
                title="Move up"
                aria-label="Move document up"
                disabled={!canMoveUp}
                onClick={(e) => {
                  e.stopPropagation();
                  if (canMoveUp) onMoveDocument(doc.Id, 'up');
                }}
                style={moveBtnStyle(canMoveUp)}
              >
                ▲
              </button>
              <button
                type="button"
                data-testid={`tree-move-down-${doc.Id}`}
                title="Move down"
                aria-label="Move document down"
                disabled={!canMoveDown}
                onClick={(e) => {
                  e.stopPropagation();
                  if (canMoveDown) onMoveDocument(doc.Id, 'down');
                }}
                style={moveBtnStyle(canMoveDown)}
              >
                ▼
              </button>
            </>
          )}
        </span>
      )}
      <span
        style={{
          ...docLeafNameStyle,
          ...(strikethrough ? { textDecoration: 'line-through', color: 'var(--text-muted)' } : null),
        }}
        title={doc.FileName}
      >
        {doc.FileName}
      </span>
      {doc.SequenceNumber !== undefined && (
        <span style={{ ...docLeafTargetStyle, fontFamily: 'monospace', flexShrink: 0 }} title="Document sequence">
          Seq {doc.SequenceNumber ?? '—'}
        </span>
      )}
      <DocumentBadge operation={doc.LifecycleOperation} size="sm" />
      {/* phase-compiler-screen-batch item 5 (Sharon, 2026-07-23) — placement
          lock state, in the TREE as well as on the centre pane's placement
          card. Two VISUALLY DISTINCT glyphs, not two weights of one: a closed
          padlock when locked, an OPEN padlock when unlocked. The unlocked state
          has to be drawn at all, or the feature is undiscoverable — you cannot
          learn that a thing can be locked from an absence.

          DISPLAY-ONLY by Sharon's ruling: the tree is for navigation and
          structure, so this is a <span>, not a control — no onClick, no
          tabIndex, aria-hidden on the glyph with the meaning carried by the
          title. The action stays on the placement card, and the tooltip says
          so. Fixed-width slot so the dense rows do not reflow as state
          changes (the sideIconCell principle, 942f37c). */}
      {doc.IsLocked !== undefined && (
        <span style={lockIconSlotBase}>
          {/* phase-tree-lock-open-shackle — the SHARED lock indicator, also used
              by the placement card. Display-only in the tree (Sharon's ruling):
              LockStateIcon renders no interactive element, and the tooltip
              points at the placement card where the state is actually changed.
              Locked → closed padlock, unlocked → OPEN padlock with the shackle
              swung clear to the side — the states differ by SHAPE, not by
              opacity/colour, so an unlocked row can never read as absent. */}
          <LockStateIcon locked={doc.IsLocked} testId={`doc-lock-${doc.Id}`} />
        </span>
      )}
      {target && (
        <span style={docLeafTargetStyle} data-testid={`section-tree-doc-target-${doc.Id}`}>
          {target}
        </span>
      )}
      {/* phase-ui-tree-leaf-review-capsules (Sharon 2026-07-18) — the DOCUMENT
          is the thing needing review; the flags render on the leaf itself,
          from the same per-document data the section roll-up reads.
          phase-compiler-tree-conf-align — the low-confidence capsule renders
          LAST (rightmost) so its fixed-width glyph pins to the row's right edge:
          every confidence capsule then shares one right-hand column across rows
          regardless of filename length or which other badges precede it. The
          "review" capsule sits immediately to its left, keeping the two review
          cues adjacent. */}
      {flags.unconfirmed && (
        <span
          style={reviewBadgeStyle}
          data-testid={`doc-unconfirmed-${doc.Id}`}
          aria-label="not yet confirmed"
          title="Not yet confirmed by a human reviewer"
        >
          ⚠ review
        </span>
      )}
      {flags.lowConfidence && (
        <span
          style={lowConfBadgeStyle}
          data-testid={`doc-low-conf-${doc.Id}`}
          aria-label="low-confidence AI placement"
          title="This document has low AI confidence — review carefully"
        >
          ◐ low conf.
        </span>
      )}
      {/* phase-compiler-screen-batch item 5 — the locked-only glyph that used to
          sit here is gone: it is superseded by the fixed-width lock SLOT above,
          which draws BOTH states (a closed padlock when locked, an open one when
          not). Leaving this in rendered a second padlock on every locked row. */}
    </div>
  );
}

function collectExpandableCodes(
  nodes: readonly SectionNode[],
  documentsByKey?: ReadonlyMap<string, readonly SectionTreeDocLeaf[]>,
): string[] {
  const out: string[] = [];
  const walk = (list: readonly SectionNode[]) => {
    for (const n of list) {
      const hasDocs = (documentsByKey?.get(sectionKey(n.code))?.length ?? 0) > 0;
      if (n.children.length > 0 || hasDocs) {
        out.push(n.code);
      }
      if (n.children.length > 0) walk(n.children);
    }
  };
  walk(nodes);
  return out;
}

interface TreeNodeProps {
  node: SectionNode;
  depth: number;
  expanded: ReadonlySet<string>;
  toggleExpanded: (code: string) => void;
  selectedCode: string | null;
  onSelect: (code: string) => void;
  onDropFiles?: (sectionCode: string, files: readonly File[], position?: DocumentDropPosition) => void;
  /** TODO-081 — document→section cross-section drag handler. */
  onDropDocument?: (documentId: string, targetSectionCode: string, position?: DocumentDropPosition) => void;
  /** TODO-065 — true when the submission's region is FDA/US. */
  isFda?: boolean;
  /** Phase node-extension-compiler-ui (TODO-048 Part B) — section codes with extensions. */
  extensionCodes?: ReadonlySet<string>;
  /** Phase phase-review-surface-strengthen — codes with unconfirmed documents. */
  unconfirmedCodes?: ReadonlySet<string>;
  /** Phase phase-review-surface-strengthen — codes with low-confidence placements. */
  lowConfidenceCodes?: ReadonlySet<string>;
  /** phase-ui-compiler-tree-document-leaves — document leaves per sectionKey. */
  documentsByKey?: ReadonlyMap<string, readonly SectionTreeDocLeaf[]>;
  selectedDocumentId?: string | null;
  onSelectDocument?: (documentId: string) => void;
  priorSeqByDocId?: ReadonlyMap<string, string>;
  onMoveDocument?: (documentId: string, direction: 'up' | 'down') => void;
}

function TreeNode({
  node,
  depth,
  expanded,
  toggleExpanded,
  selectedCode,
  onSelect,
  onDropFiles,
  onDropDocument,
  isFda,
  extensionCodes,
  unconfirmedCodes,
  lowConfidenceCodes,
  documentsByKey,
  selectedDocumentId,
  onSelectDocument,
  priorSeqByDocId,
  onMoveDocument,
}: TreeNodeProps): ReactElement {
  const [dragOver, setDragOver] = useState(false);
  const hasChildren = node.children.length > 0;
  // phase-ui-compiler-tree-document-leaves — sections with documents are
  // expandable down to their leaves.
  const docs = documentsByKey?.get(sectionKey(node.code)) ?? [];
  const hasDocs = docs.length > 0;
  const isExpandable = hasChildren || hasDocs;
  const isExpanded = expanded.has(node.code);
  const isSelected = selectedCode === node.code;
  // phase-compiler-chrome-batch item 5 — while a DOCUMENT holds the primary
  // highlight, the containing section renders only a muted tint (and is not
  // aria-selected): the tree paints exactly ONE primary selection.
  const documentHoldsPrimary = selectedDocumentId != null;
  // When leaves are on, section count badges = number of leaves.
  const badgeCount = documentsByKey ? docs.length : (node.documentCount ?? 0);

  const handleSelect = useCallback(() => {
    onSelect(node.code);
  }, [onSelect, node.code]);

  const handleCaret = useCallback(
    (e: ReactMouseEvent) => {
      e.stopPropagation();
      toggleExpanded(node.code);
    },
    [toggleExpanded, node.code],
  );

  const handleKey = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onSelect(node.code);
      } else if (e.key === 'ArrowRight' && isExpandable && !isExpanded) {
        e.preventDefault();
        toggleExpanded(node.code);
      } else if (e.key === 'ArrowLeft' && isExpandable && isExpanded) {
        e.preventDefault();
        toggleExpanded(node.code);
      }
    },
    [onSelect, toggleExpanded, node.code, isExpandable, isExpanded],
  );

  return (
    <>
      <div
        role="treeitem"
        aria-selected={isSelected && !documentHoldsPrimary}
        aria-expanded={isExpandable ? isExpanded : undefined}
        aria-level={depth + 1}
        data-testid={`section-tree-node-${node.code}`}
        data-section-drop-zone={node.code}
        tabIndex={0}
        onClick={handleSelect}
        onKeyDown={handleKey}
        onDragOver={(e) => {
          const hasFile = e.dataTransfer.types.includes('Files');
          const hasDoc = e.dataTransfer.types.includes('application/x-document-id');
          if (!hasFile && !hasDoc) return;
          if (hasFile && !onDropFiles) return;
          if (hasDoc && !onDropDocument) return;
          e.preventDefault();
          e.stopPropagation();
          e.dataTransfer.dropEffect = hasDoc ? 'move' : 'copy';
          if (!dragOver) setDragOver(true);
        }}
        onDragLeave={() => {
          if (dragOver) setDragOver(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setDragOver(false);
          // TODO-081 — document→section cross-section reclassification drop
          const docId = e.dataTransfer.getData('application/x-document-id');
          if (docId && onDropDocument) {
            onDropDocument(docId, node.code);
            return;
          }
          // Phase UI-13 — OS file→section drop
          if (!onDropFiles) return;
          onDropFiles(node.code, Array.from(e.dataTransfer.files ?? []));
        }}
        style={{
          ...nodeBaseStyle,
          paddingLeft: 6 + depth * 14,
          ...(isSelected ? (documentHoldsPrimary ? nodeContainingTintStyle : nodeSelectedStyle) : null),
          ...(dragOver
            ? {
                background: 'rgba(59,130,246,0.10)',
                outline: '1px dashed rgba(59,130,246,0.55)',
              }
            : null),
        }}
      >
        {isExpandable ? (
          <span
            style={caretStyle}
            onClick={handleCaret}
            aria-hidden="true"
            data-testid={`section-tree-caret-${node.code}`}
          >
            {isExpanded ? '▾' : '▸'}
          </span>
        ) : (
          <span style={caretStyle} aria-hidden="true" />
        )}
        <span style={nodeLabelStyle} title={node.label}>{node.label}</span>
        {/* TODO-060 — document count badge; hidden when count is 0.
            With document leaves on, the count = number of leaves. */}
        {badgeCount > 0 && (
          <span
            style={docCountStyle}
            data-testid={`section-count-${node.code}`}
            aria-label={`${badgeCount} document${badgeCount === 1 ? '' : 's'}`}
          >
            {badgeCount}
          </span>
        )}
        {/* TODO-065 — required indicator for empty FDA M1 sections */}
        {isFda === true && FDA_REQUIRED_M1.has(node.code) && badgeCount === 0 && (
          <span
            style={requiredBadgeStyle}
            data-testid={`section-required-${node.code}`}
            aria-label="required section"
          >
            required
          </span>
        )}
        {/* Phase node-extension-compiler-ui (TODO-048 Part B) — extension badge */}
        {extensionCodes?.has(node.code) && (
          <span
            style={extBadgeStyle}
            data-testid={`section-ext-badge-${node.code}`}
            aria-label="has node extensions"
          >
            ⊕ ext
          </span>
        )}
        {/* Phase phase-review-surface-strengthen — unconfirmed documents badge */}
        {unconfirmedCodes?.has(sectionKey(node.code)) && (
          <span
            style={reviewBadgeStyle}
            data-testid={`section-unconfirmed-${node.code}`}
            aria-label="has unconfirmed documents"
            title="This section has documents not yet confirmed by a human reviewer"
          >
            ⚠ review{(() => {
              const n = docs.filter((d) => docReviewFlags(d).unconfirmed).length;
              return n > 1 ? ` · ${n}` : '';
            })()}
          </span>
        )}
        {/* Phase phase-review-surface-strengthen — low-confidence placement badge.
            phase-ui-tree-leaf-review-capsules: rolled-up COUNT when the leaves
            are known, so a collapsed section says how many leaves need eyes;
            expanding reveals which (each flagged leaf carries its own capsule).
            phase-compiler-tree-conf-align — renders LAST (rightmost), exactly as
            the leaf capsule does, so the confidence capsule shares ONE stable
            right edge across section rows AND document leaves; the "review"
            capsule to its left can appear or vanish without shifting it. */}
        {lowConfidenceCodes?.has(sectionKey(node.code)) && (
          <span
            style={lowConfBadgeStyle}
            data-testid={`section-low-conf-${node.code}`}
            aria-label="has low-confidence AI placement"
            title="One or more documents have low AI confidence — review carefully"
          >
            ◐ low conf.{(() => {
              const n = docs.filter((d) => docReviewFlags(d).lowConfidence).length;
              return n > 1 ? ` · ${n}` : '';
            })()}
          </span>
        )}
      </div>
      {isExpandable && isExpanded ? (
        <div role="group">
          {node.children.map((child) => (
            <TreeNode
              key={child.code}
              node={child}
              depth={depth + 1}
              expanded={expanded}
              toggleExpanded={toggleExpanded}
              selectedCode={selectedCode}
              onSelect={onSelect}
              isFda={isFda}
              {...(onDropFiles ? { onDropFiles } : {})}
              {...(onDropDocument ? { onDropDocument } : {})}
              {...(extensionCodes ? { extensionCodes } : {})}
              {...(unconfirmedCodes ? { unconfirmedCodes } : {})}
              {...(lowConfidenceCodes ? { lowConfidenceCodes } : {})}
              {...(documentsByKey ? { documentsByKey } : {})}
              {...(selectedDocumentId !== undefined ? { selectedDocumentId } : {})}
              {...(onSelectDocument ? { onSelectDocument } : {})}
              {...(priorSeqByDocId ? { priorSeqByDocId } : {})}
              {...(onMoveDocument ? { onMoveDocument } : {})}
            />
          ))}
          {docs.map((doc, docIdx) => (
            <DocLeafRow
              key={doc.Id}
              doc={doc}
              depth={depth + 1}
              selected={selectedDocumentId === doc.Id}
              onSelectDocument={onSelectDocument}
              priorSeqByDocId={priorSeqByDocId}
              onMoveDocument={onMoveDocument}
              canMoveUp={docIdx > 0}
              canMoveDown={docIdx < docs.length - 1}
              sectionCode={node.code}
              onDropFiles={onDropFiles}
              onDropDocument={onDropDocument}
            />
          ))}
        </div>
      ) : null}
    </>
  );
}

export function SectionTree({
  tree,
  selectedCode,
  onSelect,
  loading = false,
  error = null,
  title = 'CTD Tree',
  subtitle,
  onDropFiles,
  onDropDocument,
  region,
  extensionCodes,
  unconfirmedCodes,
  lowConfidenceCodes,
  documentsByKey,
  unplacedDocuments,
  selectedDocumentId,
  onSelectDocument,
  priorSeqByDocId,
  storageKey,
  onMoveDocument,
}: SectionTreeProps): ReactElement {
  // TODO-065: derive FDA flag from the region string (supports both the raw
  // submission Region value 'FDA' and the normalised 'US').
  const isFda = region === 'FDA' || region === 'US';

  // Default: top-level modules expanded. Deeper expansion is user-driven.
  const defaultExpanded = useMemo(() => {
    const set = new Set<string>();
    for (const n of tree) {
      if (n.children.length > 0) set.add(n.code);
    }
    return set;
  }, [tree]);

  // phase-ui-compiler-tree-document-leaves — expand state is remembered
  // per session when a storageKey is provided (sessionStorage; falls back
  // to defaults when absent/unparseable).
  const readStoredExpanded = useCallback((): ReadonlySet<string> | null => {
    if (!storageKey) return null;
    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw) as unknown;
      if (Array.isArray(parsed)) return new Set(parsed.filter((x): x is string => typeof x === 'string'));
    } catch {
      /* ignore — fall back to defaults */
    }
    return null;
  }, [storageKey]);

  const [expanded, setExpanded] = useState<ReadonlySet<string>>(
    () => readStoredExpanded() ?? defaultExpanded,
  );

  // When the tree changes (e.g. data loaded after mount), re-apply the
  // default-expanded set so newly-arrived top-level modules are open —
  // unless the session remembered a state for this tree.
  const lastTreeRef = useRef<readonly SectionNode[]>(tree);
  useEffect(() => {
    if (lastTreeRef.current !== tree) {
      lastTreeRef.current = tree;
      setExpanded(readStoredExpanded() ?? defaultExpanded);
    }
  }, [tree, defaultExpanded, readStoredExpanded]);


  // Persist ONLY user-driven expand changes — effect-based persistence
  // snapshots transient pre-hydration states (an empty set before the
  // tree arrives) and poisons later session restores.
  const persistExpanded = useCallback(
    (next: ReadonlySet<string>) => {
      if (!storageKey) return;
      try {
        sessionStorage.setItem(storageKey, JSON.stringify([...next]));
      } catch {
        /* storage full/unavailable — non-fatal */
      }
    },
    [storageKey],
  );

  const toggleExpanded = useCallback((code: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      persistExpanded(next);
      return next;
    });
  }, [persistExpanded]);

  const expandAll = useCallback(() => {
    const codes = collectExpandableCodes(tree, documentsByKey);
    if ((unplacedDocuments?.length ?? 0) > 0) codes.push(UNPLACED_CODE);
    const next = new Set(codes);
    persistExpanded(next);
    setExpanded(next);
  }, [tree, documentsByKey, unplacedDocuments, persistExpanded]);

  const collapseAll = useCallback(() => {
    const next = new Set<string>();
    persistExpanded(next);
    setExpanded(next);
  }, [persistExpanded]);

  return (
    <div data-testid="section-tree" role="tree" aria-label="CTD section tree"
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes('Files')) {
          e.preventDefault();
          e.dataTransfer.dropEffect = 'none';
        }
      }}
      onDrop={(e) => {
        // Unaccepted file drops must never navigate the browser to the PDF.
        if (e.dataTransfer.types.includes('Files')) e.preventDefault();
      }}
    >
      {/* phase-compiler-chrome-batch item 1 — the one shared pane-header
          treatment across all three panes. */}
      <PaneHeaderRow title={title} testId="tree-pane-header" />
      {subtitle ? <div style={subtitleStyle}>{subtitle}</div> : null}

      {loading ? (
        <div data-testid="section-tree-loading" style={emptyStyle}>
          Loading section tree…
        </div>
      ) : error ? (
        <div data-testid="section-tree-error" style={errorStyle} role="alert">
          {error}
        </div>
      ) : tree.length === 0 ? (
        <div data-testid="section-tree-empty" style={emptyStyle}>
          No section tree available for this submission.
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 8, marginBottom: 6 }}>
            <button
              type="button"
              data-testid="section-tree-expand-all"
              onClick={expandAll}
              style={{
                fontSize: 11,
                padding: '2px 6px',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 4,
                cursor: 'pointer',
                color: 'var(--text-muted)',
              }}
            >
              Expand all
            </button>
            <button
              type="button"
              data-testid="section-tree-collapse-all"
              onClick={collapseAll}
              style={{
                fontSize: 11,
                padding: '2px 6px',
                background: 'transparent',
                border: '1px solid var(--border)',
                borderRadius: 4,
                cursor: 'pointer',
                color: 'var(--text-muted)',
              }}
            >
              Collapse all
            </button>
          </div>
          {(unplacedDocuments?.length ?? 0) > 0 && (
            <>
              <div
                role="treeitem"
                aria-expanded={expanded.has(UNPLACED_CODE)}
                aria-level={1}
                tabIndex={0}
                data-testid="section-tree-node-unplaced"
                onClick={() => toggleExpanded(UNPLACED_CODE)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    toggleExpanded(UNPLACED_CODE);
                  }
                }}
                style={{ ...nodeBaseStyle, fontStyle: 'italic', color: 'var(--text-muted)' }}
              >
                <span
                  style={caretStyle}
                  aria-hidden="true"
                  data-testid="section-tree-caret-unplaced"
                >
                  {expanded.has(UNPLACED_CODE) ? '▾' : '▸'}
                </span>
                <span style={{ flex: 1 }}>Unplaced — not yet classified</span>
                <span
                  style={docCountStyle}
                  data-testid="section-count-unplaced"
                  aria-label={`${unplacedDocuments?.length ?? 0} unplaced documents`}
                >
                  {unplacedDocuments?.length ?? 0}
                </span>
              </div>
              {expanded.has(UNPLACED_CODE) && (
                <div role="group">
                  {(unplacedDocuments ?? []).map((doc, docIdx) => (
                    <DocLeafRow
                      key={doc.Id}
                      doc={doc}
                      depth={1}
                      selected={selectedDocumentId === doc.Id}
                      onSelectDocument={onSelectDocument}
                      priorSeqByDocId={priorSeqByDocId}
                      onMoveDocument={onMoveDocument}
                      canMoveUp={docIdx > 0}
                      canMoveDown={docIdx < (unplacedDocuments?.length ?? 0) - 1}
                      onDropDocument={onDropDocument}
                    />
                  ))}
                </div>
              )}
            </>
          )}
          {tree.map((node) => (
            <TreeNode
              key={node.code}
              node={node}
              depth={0}
              expanded={expanded}
              toggleExpanded={toggleExpanded}
              selectedCode={selectedCode}
              onSelect={onSelect}
              isFda={isFda}
              {...(onDropFiles ? { onDropFiles } : {})}
              {...(onDropDocument ? { onDropDocument } : {})}
              {...(extensionCodes ? { extensionCodes } : {})}
              {...(unconfirmedCodes ? { unconfirmedCodes } : {})}
              {...(lowConfidenceCodes ? { lowConfidenceCodes } : {})}
              {...(documentsByKey ? { documentsByKey } : {})}
              {...(selectedDocumentId !== undefined ? { selectedDocumentId } : {})}
              {...(onSelectDocument ? { onSelectDocument } : {})}
              {...(priorSeqByDocId ? { priorSeqByDocId } : {})}
              {...(onMoveDocument ? { onMoveDocument } : {})}
            />
          ))}
        </>
      )}
    </div>
  );
}
